import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Send, Upload, X, Loader2, MessageCircle, DollarSign, FileText, Image as ImageIcon, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuotePortal = () => {
  const { accessToken } = useParams();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quote, setQuote] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    fetchQuoteData();
  }, [accessToken]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchQuoteData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/quotes/portal/${accessToken}`);
      if (!response.ok) {
        throw new Error('Quote not found or link expired');
      }
      const data = await response.json();
      setQuote(data.quote);
      setMessages(data.messages || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
  const allowedDocTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  const allowedTypes = [...allowedImageTypes, ...allowedDocTypes];

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return <ImageIcon className="w-4 h-4" />;
    if (fileType === 'application/pdf') return <FileText className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    if (uploadedFiles.length + files.length > 5) {
      toast({
        title: 'Too many files',
        description: 'Maximum 5 files per message',
        variant: 'destructive'
      });
      return;
    }
    
    setIsUploading(true);
    const newFiles = [];
    
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: `${file.name}: Only images and documents (PDF, DOC) allowed`,
          variant: 'destructive'
        });
        continue;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: `${file.name}: Maximum 10MB`,
          variant: 'destructive'
        });
        continue;
      }
      
      let preview = null;
      if (file.type.startsWith('image/')) {
        preview = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });
      }
      
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      try {
        const response = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          body: uploadFormData
        });
        
        if (response.ok) {
          const data = await response.json();
          newFiles.push({
            id: Date.now() + Math.random(),
            name: file.name,
            type: file.type,
            url: data.url,
            preview
          });
        }
      } catch (err) {
        console.error('Upload error:', err);
      }
    }
    
    if (newFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
    
    setIsUploading(false);
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && uploadedFiles.length === 0) return;
    
    setIsSending(true);
    
    try {
      const response = await fetch(`${API_URL}/api/quotes/portal/${accessToken}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: newMessage,
          attachments: uploadedFiles.map(f => ({
            url: f.url,
            name: f.name,
            type: f.type
          }))
        })
      });
      
      if (response.ok) {
        toast({
          title: 'Message sent!',
          description: 'Your reply has been sent to the team'
        });
        setNewMessage('');
        setUploadedFiles([]);
        fetchQuoteData(); // Refresh messages
      } else {
        throw new Error('Failed to send message');
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSending(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-400 mx-auto mb-4" />
          <p className="text-white text-lg">Loading your quote...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900 to-gray-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 bg-black/50 border-red-500/50 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Link Invalid or Expired</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <a 
            href="/"
            className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Return to Homepage
          </a>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Your Quote Request
          </h1>
          <p className="text-purple-300">
            View responses and communicate with our team
          </p>
        </div>

        {/* Quote Summary Card */}
        <Card className="p-6 mb-6 bg-black/40 border-purple-500/30">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-xl font-semibold text-white">{quote.name}</h2>
              <p className="text-gray-400">{quote.email}</p>
              {quote.company && <p className="text-gray-500 text-sm">{quote.company}</p>}
            </div>
            <div className="text-right">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                quote.status === 'new' ? 'bg-green-500/20 text-green-400' :
                quote.status === 'responded' ? 'bg-blue-500/20 text-blue-400' :
                quote.status === 'customer_replied' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-gray-500/20 text-gray-400'
              }`}>
                {quote.status === 'new' ? 'Pending Review' :
                 quote.status === 'responded' ? 'Response Received' :
                 quote.status === 'customer_replied' ? 'Awaiting Response' :
                 quote.status}
              </span>
              <p className="text-gray-500 text-sm mt-1">{formatDate(quote.created_at)}</p>
            </div>
          </div>
          
          <div className="border-t border-purple-500/20 pt-4">
            <p className="text-sm text-purple-400 mb-1">Sign Type: {quote.sign_type}</p>
            <p className="text-gray-300">{quote.message}</p>
          </div>
          
          {/* Original Attachments */}
          {quote.attachments && quote.attachments.length > 0 && (
            <div className="mt-4 pt-4 border-t border-purple-500/20">
              <p className="text-sm text-gray-400 mb-2">Your attachments:</p>
              <div className="flex flex-wrap gap-2">
                {quote.attachments.map((att, idx) => (
                  <a
                    key={idx}
                    href={att.url.startsWith('http') ? att.url : `${API_URL}${att.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-purple-500/10 rounded-lg text-purple-300 hover:bg-purple-500/20 transition-colors"
                  >
                    {getFileIcon(att.type)}
                    <span className="text-sm truncate max-w-32">{att.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Conversation */}
        <Card className="bg-black/40 border-purple-500/30 overflow-hidden">
          <div className="p-4 border-b border-purple-500/20">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-purple-400" />
              Conversation
            </h3>
          </div>
          
          {/* Messages */}
          <div className="max-h-96 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">No messages yet. Our team will respond soon!</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-4 ${
                      msg.sender_type === 'customer'
                        ? 'bg-purple-600/30 border border-purple-500/30'
                        : 'bg-green-600/20 border border-green-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-sm font-medium ${
                        msg.sender_type === 'customer' ? 'text-purple-300' : 'text-green-300'
                      }`}>
                        {msg.sender_type === 'customer' ? 'You' : 'Okinawa Sign Studio'}
                      </span>
                      <span className="text-xs text-gray-500">{formatDate(msg.created_at)}</span>
                    </div>
                    
                    <p className="text-gray-200">{msg.message}</p>
                    
                    {/* Price Quote */}
                    {msg.price_quote && (
                      <div className="mt-3 p-3 bg-green-500/20 rounded-lg border border-green-500/30">
                        <div className="flex items-center gap-2 text-green-400">
                          <DollarSign className="w-5 h-5" />
                          <span className="font-semibold text-lg">
                            Quoted Price: ¥{msg.price_quote.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Message Attachments */}
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {msg.attachments.map((att, idx) => (
                          <a
                            key={idx}
                            href={att.url.startsWith('http') ? att.url : `${API_URL}${att.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 bg-black/30 rounded-lg text-gray-300 hover:bg-black/50 transition-colors"
                          >
                            {getFileIcon(att.type)}
                            <span className="text-sm truncate max-w-24">{att.name}</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Reply Form */}
          <div className="p-4 border-t border-purple-500/20 bg-black/20">
            {/* File Previews */}
            {uploadedFiles.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {uploadedFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-2 px-3 py-2 bg-purple-500/20 rounded-lg"
                  >
                    {getFileIcon(file.type)}
                    <span className="text-sm text-purple-300 truncate max-w-24">{file.name}</span>
                    <button
                      onClick={() => removeFile(file.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex gap-3">
              <div className="flex-1">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your reply..."
                  className="bg-black/30 border-purple-500/30 text-white placeholder-gray-500 resize-none"
                  rows={2}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="border-purple-500/30 text-purple-400 hover:bg-purple-500/20"
                >
                  {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                </Button>
                <Button
                  onClick={handleSendMessage}
                  disabled={isSending || (!newMessage.trim() && uploadedFiles.length === 0)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*,.pdf,.doc,.docx"
              multiple
              onChange={(e) => handleFileUpload(Array.from(e.target.files))}
            />
          </div>
        </Card>
        
        {/* Footer Link */}
        <div className="text-center mt-8">
          <a 
            href="/"
            className="text-purple-400 hover:text-purple-300 inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Okinawa Sign Studio
          </a>
        </div>
      </div>
    </div>
  );
};

export default QuotePortal;
